/**
 * D-pad focus navigation.
 *
 * Chromium implements neither CSS `nav-up`/`nav-down` nor spatial navigation, and
 * DOM order is the wrong model for a mosaic: the tile visually to the right of
 * the main pane is not necessarily the next element in the document. So focus
 * moves by geometry instead.
 */

export type Direction = 'up' | 'down' | 'left' | 'right';

const FOCUSABLE = '[data-focusable]:not([disabled]):not([aria-hidden="true"])';

/**
 * How much lateral drift costs relative to travel along the direction of
 * movement. High enough that a roughly-aligned neighbour always wins over a
 * closer but badly offset one, which is what feels right on a remote.
 */
const CROSS_AXIS_PENALTY = 3;

function isVisible(el: HTMLElement): boolean {
  if (el.hidden) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  const style = getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
}

function candidates(scope: ParentNode): HTMLElement[] {
  return [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(isVisible);
}

interface Point { x: number; y: number }

const centerOf = (r: DOMRect): Point => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });

/**
 * Distance along the axis of travel, and the perpendicular drift. Edges rather
 * than centres are used along the axis so that a tall neighbour is not penalised
 * for its height.
 */
function score(from: DOMRect, to: DOMRect, dir: Direction): number | null {
  const a = centerOf(from);
  const b = centerOf(to);

  let along: number;
  let across: number;

  switch (dir) {
    case 'left':
      along = from.left - to.right;
      across = Math.abs(a.y - b.y);
      break;
    case 'right':
      along = to.left - from.right;
      across = Math.abs(a.y - b.y);
      break;
    case 'up':
      along = from.top - to.bottom;
      across = Math.abs(a.x - b.x);
      break;
    case 'down':
      along = to.top - from.bottom;
      across = Math.abs(a.x - b.x);
      break;
  }

  // Overlapping edges give a small negative value; treat touching as zero but
  // reject anything genuinely behind the current element.
  if (along < -Math.min(from.width, from.height) / 2) return null;

  return Math.max(along, 0) + across * CROSS_AXIS_PENALTY;
}

export function currentFocus(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active.matches(FOCUSABLE) ? active : null;
}

export function focusElement(el: HTMLElement): void {
  el.focus({ preventScroll: true });
  // `nearest` keeps a filmstrip from jumping the main pane out of view.
  el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
}

export function focusFirst(scope: ParentNode = document): boolean {
  const list = candidates(scope);
  // Honour an explicit entry point, so opening a panel lands somewhere sensible.
  const preferred = list.find((el) => el.dataset.focusDefault !== undefined) ?? list[0];
  if (!preferred) return false;
  focusElement(preferred);
  return true;
}

export function move(dir: Direction, scope: ParentNode = document): boolean {
  const current = currentFocus();
  if (!current) return focusFirst(scope);

  const from = current.getBoundingClientRect();
  let best: HTMLElement | null = null;
  let bestScore = Infinity;

  for (const el of candidates(scope)) {
    if (el === current) continue;
    const s = score(from, el.getBoundingClientRect(), dir);
    if (s === null) continue;
    if (s < bestScore) {
      bestScore = s;
      best = el;
    }
  }

  if (!best) return false;
  focusElement(best);
  return true;
}
